import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n";
import { inr } from "@/api";
import { LOAN_PLAN_TABLE } from "@/constants";

const monthlyRate = (a) => (a < 150000 ? 11 : a < 300000 ? 12 : a < 600000 ? 13 : a < 1000000 ? 14 : 15);

const TABS = [
  { id: "daily_recurring", min: 50, max: 9000, step: 50, def: 500 },
  { id: "daily_roi", min: 2000, max: 500000, step: 1000, def: 50000 },
  { id: "monthly_roi", min: 5000, max: 1200000, step: 5000, def: 100000 },
  { id: "loan_plan", min: 2000, max: 500000, step: 1000, def: 25000 },
];

const Row = ({ label, value, strong, gold }) => (
  <div className={`flex items-baseline justify-between py-3 border-b border-[#E5E2D9] last:border-0 ${strong ? "pt-5" : ""}`}>
    <span className={`text-sm ${strong ? "font-bold text-[#1A1A1A]" : "text-[#5C5C5C]"}`}>{label}</span>
    <span
      className={`tabular font-bold ${
        strong ? "font-display text-3xl sm:text-4xl" : "text-lg"
      } ${gold ? "text-[#B5952F]" : "text-[#1A1A1A]"}`}
    >
      {value}
    </span>
  </div>
);

export const Calculator = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("daily_recurring");
  const [amounts, setAmounts] = useState({ daily_recurring: 500, daily_roi: 50000, monthly_roi: 100000, loan_plan: 25000 });
  const amount = amounts[tab];
  const cfg = TABS.find((x) => x.id === tab);

  const setAmount = (v) => setAmounts((s) => ({ ...s, [tab]: v }));

  let rows = [];
  if (tab === "daily_recurring") {
    const total = amount * 30;
    const profit = total * 0.1;
    rows = [
      { label: t("calc_duration"), value: `30 ${t("calc_days")}` },
      { label: t("calc_rate"), value: "10%" },
      { label: t("calc_total_deposit"), value: inr(total) },
      { label: `${t("calc_profit")} (+)`, value: inr(profit), gold: true },
      { label: t("calc_total_return"), value: inr(total + profit), strong: true },
    ];
  } else if (tab === "daily_roi") {
    const rate = amount >= 100000 ? 2 : 1;
    const days = amount >= 100000 ? 60 : 90;
    const daily = amount * (rate / 100);
    rows = [
      { label: t("calc_duration"), value: `${days} ${t("calc_days")}` },
      { label: t("calc_rate"), value: `${rate}% / ${t("calc_days").replace("s", "")}` },
      { label: t("calc_daily_income"), value: inr(daily), gold: true },
      { label: `${t("calc_profit")} (+)`, value: inr(daily * days), gold: true },
      { label: t("calc_capital_back"), value: inr(amount) },
      { label: t("calc_total_return"), value: inr(amount + daily * days), strong: true },
    ];
  } else if (tab === "loan_plan") {
    const rate = amount >= 100000 ? 2 : 1;
    const daily = amount * (rate / 100);
    const slab = [...LOAN_PLAN_TABLE].reverse().find((r) => amount >= r.deposit);
    rows = [
      { label: t("calc_duration"), value: `90 ${t("calc_days")}` },
      { label: t("calc_rate"), value: `${rate}% / ${t("calc_days").replace("s", "")}` },
      { label: t("calc_daily_income"), value: inr(daily), gold: true },
      { label: `${t("calc_profit")} (+)`, value: inr(daily * 90), gold: true },
      { label: t("calc_capital_back"), value: inr(amount) },
      ...(slab ? [{ label: t("calc_loan_upto"), value: inr(slab.loan) }] : []),
      { label: t("calc_total_return"), value: inr(amount + daily * 90), strong: true },
    ];
  } else {
    const rate = monthlyRate(amount);
    const mi = amount * (rate / 100);
    rows = [
      { label: t("calc_duration"), value: `12 ${t("calc_months")}` },
      { label: t("calc_rate"), value: `${rate}% / ${t("calc_months").replace("s", "")}` },
      { label: t("calc_monthly_income"), value: inr(mi), gold: true },
      { label: `${t("calc_profit")} (+)`, value: inr(mi * 12), gold: true },
      { label: t("calc_capital_back"), value: inr(amount) },
      { label: t("calc_total_return"), value: inr(amount + mi * 12), strong: true },
    ];
  }

  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
      <div className="lg:col-span-7">
        <div className="flex border border-[#1A1A1A] w-fit" data-testid="calculator-tabs">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`calc-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-bold tracking-wide transition-colors duration-200 ${
                tab === tb.id ? "bg-[#1A1A1A] text-[#D4AF37]" : "text-[#1A1A1A] hover:bg-[#F3F1EB]"
              }`}
            >
              {t(`${{ daily_recurring: "p1", daily_roi: "p2", monthly_roi: "p3", loan_plan: "p6" }[tb.id]}_name`)}
            </button>
          ))}
        </div>

        <div className="mt-12">
          <div className="flex items-baseline justify-between mb-6">
            <label className="text-sm font-semibold uppercase tracking-[0.15em] text-[#5C5C5C]">
              {tab === "daily_recurring" ? t("calc_daily_deposit") : t("calc_investment")}
            </label>
            <span className="font-display text-4xl sm:text-5xl font-bold tabular text-[#1A1A1A]" data-testid="calculator-amount-display">
              {inr(amount)}
            </span>
          </div>
          <input
            type="range"
            min={cfg.min}
            max={cfg.max}
            step={cfg.step}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            className="gold-range w-full"
            data-testid="calculator-slider"
          />
          <div className="flex justify-between mt-3 text-xs font-semibold text-[#5C5C5C] tabular">
            <span>{inr(cfg.min)}</span>
            <span>{inr(cfg.max)}</span>
          </div>
        </div>
      </div>

      <motion.div
        key={tab + amount}
        initial={{ opacity: 0.6, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="lg:col-span-5 bg-white border border-[#E5E2D9] p-8 sm:p-10 shadow-[8px_8px_0_0_#F3F1EB]"
        data-testid="calculator-results"
      >
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B5952F] mb-4">
          {t("calc_title")}
        </div>
        {rows.map((r) => (
          <Row key={r.label} {...r} />
        ))}
        <Link
          to="/register"
          data-testid="calculator-cta-button"
          className="mt-8 block text-center bg-[#D4AF37] text-[#1A1A1A] py-4 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200"
        >
          {t("calc_cta")}
        </Link>
      </motion.div>
    </div>
  );
};
